import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import type { FormEvent } from 'react';

import {
  type DocumentSettings,
  documentSettingsQuery,
  openFile,
  saveTerms,
  updateEntityContact,
  uploadLogo,
} from '../api/documents.ts';
import { meQuery } from '../api/me.ts';
import { dayWithYear, tenantDay } from '../api/quotes.ts';
import { Button } from '../components/button.tsx';
import { Field } from '../components/field.tsx';
import { PageHeader } from '../components/page-header.tsx';
import { TextArea } from '../components/text-area.tsx';

// CB-REQ DOC-01 and FIN-22 at v1.959: what the documents print, per trading entity — its logo, its
// address, phone and email, how to pay, and its terms of hire. Each save of the terms is a new
// version, and every document records the version it printed. The legal name and ABN are set when
// the company is provisioned and are shown here, not edited.

export function DocumentsScreen() {
  const settings = useQuery(documentSettingsQuery);
  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        kicker="Documents"
        title="Documents"
        meta="What quotes, invoices and dockets print"
      />
      {settings.isPending ? <p className="text-muted">Loading…</p> : null}
      {settings.isError ? (
        <p role="alert" className="alert">
          {settings.error.message}
        </p>
      ) : null}
      {settings.data?.map((s) => (
        <EntitySettings key={s.entity.id} settings={s} />
      ))}
    </div>
  );
}

function EntitySettings({ settings: s }: { settings: DocumentSettings }) {
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: documentSettingsQuery.queryKey });
  return (
    <section className="panel flex flex-col gap-5" aria-labelledby={`entity-${s.entity.id}`}>
      <div>
        <h2 id={`entity-${s.entity.id}`} className="section-title">
          {s.entity.legalName}
        </h2>
        <p className="text-sm text-ink-soft">
          {s.entity.abn
            ? `ABN ${s.entity.abn}`
            : 'No ABN yet — invoices can’t be issued until it is set.'}
          {s.entity.isDefault ? ' · Heads quotes, pick lists and dockets' : ''}
        </p>
      </div>
      <Logo settings={s} onChanged={refresh} />
      <Contact settings={s} onChanged={refresh} />
      <Terms settings={s} onChanged={refresh} />
    </section>
  );
}

function Logo({
  settings: s,
  onChanged,
}: {
  settings: DocumentSettings;
  onChanged: () => Promise<void>;
}) {
  const picker = useRef<HTMLInputElement>(null);
  const upload = useMutation({
    mutationFn: (file: File) => uploadLogo(s.entity.id, file),
    onSuccess: onChanged,
  });
  const open = useMutation({ mutationFn: openFile });
  return (
    <div className="flex flex-col gap-1.5">
      <h3 className="text-sm font-semibold">Logo</h3>
      <p className="text-sm text-ink-soft">
        {s.logo ? (
          <>
            {s.logo.filename} heads every document.{' '}
            <button
              type="button"
              className="link cursor-pointer"
              onClick={() => open.mutate(s.logo!.id)}
            >
              Download
            </button>
          </>
        ) : (
          'No logo yet: documents are headed by the name in type.'
        )}
      </p>
      <div>
        <Button variant="quiet" disabled={upload.isPending} onClick={() => picker.current?.click()}>
          {upload.isPending ? 'Uploading…' : s.logo ? 'Replace logo…' : 'Upload logo…'}
        </Button>
        <input
          ref={picker}
          type="file"
          accept="image/png,image/svg+xml"
          className="sr-only"
          aria-label="Choose a logo, PNG or SVG"
          onChange={(e) => {
            const file = e.currentTarget.files?.[0];
            e.currentTarget.value = '';
            if (file) upload.mutate(file);
          }}
        />
      </div>
      <p className="text-xs text-muted">A PNG or an SVG, at most 2 MB. Wide logos print best.</p>
      {(upload.error ?? open.error) ? (
        <p role="alert" className="text-sm font-semibold text-accent-2-700">
          {(upload.error ?? open.error)!.message}
        </p>
      ) : null}
    </div>
  );
}

function Contact({
  settings: s,
  onChanged,
}: {
  settings: DocumentSettings;
  onChanged: () => Promise<void>;
}) {
  const [address, setAddress] = useState(s.entity.address ?? '');
  const [phone, setPhone] = useState(s.entity.phone ?? '');
  const [email, setEmail] = useState(s.entity.email ?? '');
  const [payment, setPayment] = useState(s.entity.paymentDetails ?? '');
  const save = useMutation({
    mutationFn: () =>
      updateEntityContact(s.entity.id, {
        address: address.trim() || null,
        phone: phone.trim() || null,
        email: email.trim() || null,
        paymentDetails: payment.trim() || null,
      }),
    onSuccess: onChanged,
  });
  function submit(event: FormEvent) {
    event.preventDefault();
    save.mutate();
  }
  return (
    <form onSubmit={submit} className="flex flex-col gap-3">
      <h3 className="text-sm font-semibold">Details on every document</h3>
      <div className="grid gap-4 sm:grid-cols-2">
        <TextArea
          label="Address"
          value={address}
          onChange={(e) => setAddress(e.currentTarget.value)}
        />
        <div className="flex flex-col gap-4">
          <Field
            label="Phone"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.currentTarget.value)}
          />
          <Field
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
          />
        </div>
      </div>
      <TextArea
        label="How to pay"
        hint="Printed at the foot of every invoice: bank, BSB, account, and what to use as the reference."
        value={payment}
        onChange={(e) => setPayment(e.currentTarget.value)}
      />
      {save.error ? (
        <p role="alert" className="text-sm font-semibold text-accent-2-700">
          {save.error.message}
        </p>
      ) : null}
      <div>
        <Button type="submit" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : save.isSuccess ? 'Saved' : 'Save'}
        </Button>
      </div>
    </form>
  );
}

function Terms({
  settings: s,
  onChanged,
}: {
  settings: DocumentSettings;
  onChanged: () => Promise<void>;
}) {
  const zone = useQuery(meQuery).data?.tenant.defaultTz;
  const [body, setBody] = useState(s.terms?.body ?? '');
  const [problem, setProblem] = useState<string | undefined>();
  const save = useMutation({
    mutationFn: () => saveTerms(s.entity.id, body),
    onSuccess: onChanged,
  });
  const changed = body.trim() !== (s.terms?.body ?? '').trim();
  function submit(event: FormEvent) {
    event.preventDefault();
    if (!body.trim()) return setProblem('Terms need some text.');
    if (!changed) return setProblem('Nothing has changed since the last version.');
    setProblem(undefined);
    save.mutate();
  }
  return (
    <form onSubmit={submit} className="flex flex-col gap-3">
      <h3 className="text-sm font-semibold">Terms of hire</h3>
      <p className="text-sm text-ink-soft">
        {s.terms
          ? `Version ${s.terms.version}${zone ? `, saved ${dayWithYear(tenantDay(s.terms.savedAt, zone))}` : ''}. Saving makes a new version; documents already prepared keep the one they printed.`
          : 'No terms yet. Quotes and dockets print them once they are set.'}
      </p>
      <TextArea
        label="Terms"
        hint="Start a line with “# ” to make it a heading. A blank line starts a new paragraph."
        rows={14}
        value={body}
        error={problem}
        onChange={(e) => setBody(e.currentTarget.value)}
      />
      {save.error ? (
        <p role="alert" className="text-sm font-semibold text-accent-2-700">
          {save.error.message}
        </p>
      ) : null}
      <div>
        <Button type="submit" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save new version'}
        </Button>
      </div>
    </form>
  );
}
